import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import { entityMemberApi } from "_shared/entity-members/_api/entity-member-api";
import EntityMember from "_shared/entity-members/entity-member";
import { EntityMemberJSON } from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";

type EntityMemberStoreState = {
  byLegalEntityId: {
    entries: Record<string, EntityMemberJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
};

const initialState: EntityMemberStoreState = {
  byLegalEntityId: {
    entries: {},
    loading: {},
    error: {},
  }
};
export const populateEntityMembersByLegalEntityId = ({ legalEntityId }: { session: Session; legalEntityId: Guid }) =>
  entityMemberApi.endpoints.getEntityMembersByEntityId.initiate({ legalEntityId: legalEntityId.value });

export const entityMembersSlice = createSlice({
  name: "entityMembers",
  initialState,
  reducers: {
    addEntityMember: (state, action: PayloadAction<EntityMember>) => {
      const id: Guid = action.payload.id;
      if (!id) return;
      // Invalidate query cache
      state.byLegalEntityId = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeEntityMember: (state, action: PayloadAction<EntityMember | Guid>) => {
      let memberId: Guid | undefined;
      if (action.payload instanceof Guid) {
        memberId = action.payload;
      } else {
        memberId = action.payload.id;
      }
      if (!memberId) {
        throw new Error("Entity member id not found");
      }
      state.byLegalEntityId = {
        entries: {},
        loading: {},
        error: {},
      };
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(entityMemberApi.endpoints.getEntityMembersByEntityId.matchPending, (state, action) => {
      const entityId = action.meta.arg.originalArgs.legalEntityId;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = true;
      state.byLegalEntityId.error[entityId] = null;
    });
    builder.addMatcher(entityMemberApi.endpoints.getEntityMembersByEntityId.matchFulfilled, (state, action) => {
      const entityId = action.meta.arg.originalArgs.legalEntityId;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = false;
      state.byLegalEntityId.entries[entityId] = action.payload;
    });

    builder.addMatcher(entityMemberApi.endpoints.getEntityMembersByEntityId.matchRejected, (state, action) => {
      const entityId = action.meta.arg.originalArgs.legalEntityId;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = false;
      state.byLegalEntityId.error[entityId] = "Error fetching entity members: " + (action.error.message ?? "An error occurred.");
    });

  },
});

const selectEntityMembers =
  createSelector(
    [
      (state: RootState) => state.entityMembers.byLegalEntityId.entries,
      (_: RootState, entityId: Guid) => entityId.value,
    ],
    (entries, entityId) => {
      const members = entries[entityId];
      if (!members) return [];
      return EntityMember.fromJSONArray({ jsonArray: members }) || [];
    }
  );
export const getEntityMembers = (entityId: Guid) => (state: RootState) =>
  selectEntityMembers(state, entityId);

export const getEntityMembersIsLoading = (entityId: Guid) => (state: RootState) => {
  return state.entityMembers.byLegalEntityId.loading[entityId.value] ?? false;
};
export const getEntityMembersIsLoaded = (entityId: Guid) => (state: RootState) =>
  Object.hasOwn(state.entityMembers.byLegalEntityId.entries, entityId.value);
export const getEntityMembersError =
  (entityId: Guid) =>
    (state: RootState): SerializedError | null => {
      const error = state.entityMembers.byLegalEntityId.error[entityId.value];
      if (error == null) return null;
      if (typeof error === 'string') {
        return { name: 'Error', message: error } as SerializedError;
      }
      return error;
    };
